/*
  Состав должников на карточке счёта с внесением и правкой связи (20.10.2026).

  Обёртка над `AccountDebtors`: карточки людей и правка их сведений остаются там, а
  внесение должника, правка связи со счётом (роль, ответственность, доля, срок) и
  удаление из состава - здесь. Связь берётся у сервера списком (`GET .../participants`)
  и сопоставляется с карточкой по человеку: карточка счёта идентификатора связи не
  отдаёт, а заводить второе поле на двух концах ради кнопки незачем.

  Счёт в суде состав не меняет: сервер откажет словами, а окно покажет отказ внутри
  себя. Права те же, что у правки сведений о человеке (право собирать документы).
*/

import { useCallback, useState } from 'react'

import { api } from '../../api/client.js'
import AccountDebtors from '../AccountDebtors.jsx'
import { Notice } from '../Common.jsx'
import ParticipantDialog from './ParticipantDialog.jsx'
import RemoveDialog from './RemoveDialog.jsx'
import useEntryList from './useEntryList.js'

export default function AccountParticipants({ card, canEdit, onDone }) {
  const accountId = card.account_id
  const load = useCallback(() => api.participants(accountId), [accountId])
  const { rows, error, reload } = useEntryList(load)
  const [dialog, setDialog] = useState(null)
  const [missing, setMissing] = useState(false)

  const linkOf = (debtor) => (rows ?? []).find((row) => row.person.person_id === debtor.person_id)
  const saved = (text) => () => {
    reload()
    onDone(text)
  }
  /* Аудит 28.10.2026, раздел 5.3: если список связей не загрузился, «Изменить связь» и «Убрать
     из состава» молча ничего не делали. Теперь человек видит причину и может повторить. */
  // Пока список связей грузится, «не нашлась» было бы неправдой (ревью 30.10.2026): надпись
  // вставала на быстрое нажатие и оставалась после загрузки. Нажатие до загрузки - пусто.
  const open = (kind) => (debtor) => {
    const link = linkOf(debtor)
    if (link) setDialog({ kind, link })
    else if (rows) setMissing(true)
  }

  return (
    <>
      {error || missing ? (
        <>
          <Notice error={error} kind="warn">
            {error ? null : 'Связь этого должника со счётом не нашлась в списке.'}
          </Notice>
          <div className="row row--tight">
            <button
              type="button"
              className="button button--quiet"
              onClick={() => {
                setMissing(false)
                reload()
              }}
            >
              Загрузить список связей заново
            </button>
          </div>
        </>
      ) : null}
      <AccountDebtors
        debtors={card.debtors}
        canEdit={canEdit}
        onSaved={() => onDone('Сведения о должнике сохранены.')}
        onAdd={canEdit ? () => setDialog({ kind: 'add' }) : undefined}
        onEditLink={canEdit ? open('edit') : undefined}
        onRemoveLink={canEdit ? open('remove') : undefined}
      />

      {dialog?.kind === 'add' || dialog?.kind === 'edit' ? (
        <ParticipantDialog
          accountId={accountId}
          link={dialog.link}
          onSaved={saved(dialog.kind === 'add' ? 'Должник внесён.' : 'Связь со счётом исправлена.')}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'remove' ? (
        <RemoveDialog
          title="Убрать должника из состава"
          action="Убрать из состава"
          onRemove={() => api.removeParticipant(accountId, dialog.link.link_id)}
          onDone={saved('Должник убран из состава счёта.')}
          onClose={() => setDialog(null)}
        >
          {dialog.link.person.full_name} перестанет быть должником по этому счёту. Сведения о
          человеке остаются: он может отвечать и по другому счёту. От состава зависит число
          заявлений и пошлин по счёту.
        </RemoveDialog>
      ) : null}
    </>
  )
}
